import { useStore } from '@/store/useStore';
import type { SoftReloadOptions } from '@/lostHooks/softReload/useSoftReload';
import { SOFT_RELOAD_PRESET } from '@/lostHooks/softReload/softReloadPresets';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import {
  TRACK_USER_ACTION_EVENT,
  trackUserAction,
} from '@/utilities/logger/amplitude/tracking';
import type { NavigateFunction } from 'react-router';
import { isEmbeddedQueryRequested } from '@/logic/embedded-mode';
import { resolveRouterBasename } from '@/utilities/helpers/same-origin-path';

export const LAUNCH_METHOD_STORAGE_KEY = 'launchMethod';
export const LAUNCH_METHOD_UNIFIED_MODULE = 'unified-module';

type SoftReloadFn = (arg?: boolean | SoftReloadOptions) => void;

export type NextGameNavigateTarget =
  | string
  | {
      pathname: string;
      search?: string;
    };

const trackPlayNow = (nextGameUrl: string | null) => {
  Logger.logRumAction({
    name: '[GW][RECENTLY_PLAYED] Clicked Play Now',
    useCase: DdEventCategory.UserInteraction,
    eventType: DdEventType.Action,
    context: {
      linkUrl: nextGameUrl,
      module: 'RECENTLY_PLAYED',
      zoneName: 'DASHBOARD',
    },
  });

  trackUserAction({
    event: TRACK_USER_ACTION_EVENT.IN_GAME_CTA_CLICKED,
    module: 'RECENTLY_PLAYED',
    zone: 'DASHBOARD',
    linkUrl: nextGameUrl,
    linkText: 'launch_game',
  });
};

/** Route target for the next game — matches existing query vs short-link URL shapes. */
export const resolveNextGameNavigateTarget = (
  nextGameUid: string,
): NextGameNavigateTarget => {
  const embeddedSearch = isEmbeddedQueryRequested() ? '&embedded=1' : '';

  if (window.location.search.includes('gameUid')) {
    return {
      pathname: '/',
      search: `?gameUid=${encodeURIComponent(nextGameUid)}${embeddedSearch}`,
    };
  }

  const pathname = `/${encodeURIComponent(nextGameUid)}`;
  return embeddedSearch
    ? { pathname, search: `?${embeddedSearch.slice(1)}` }
    : pathname;
};

const applyHistoryReplace = (target: NextGameNavigateTarget): void => {
  const basename = resolveRouterBasename(window.location.pathname);
  const url = new URL(window.location.href);

  if (typeof target === 'string') {
    url.pathname = `${basename}${target}`.replace(/\/{2,}/g, '/') || '/';
    url.search = '';
  } else {
    url.pathname =
      `${basename}${target.pathname}`.replace(/\/{2,}/g, '/') || '/';
    url.search = target.search ?? '';
  }

  window.history.replaceState(window.history.state, '', url);
};

/**
 * Updates React Router (and the address bar) without a document reload.
 * Applies replaceState first so the URL is current before soft reload store resets.
 */
export const syncNextGameRoute = (
  nextGameUid: string,
  navigate?: NavigateFunction,
): void => {
  const target = resolveNextGameNavigateTarget(nextGameUid);

  applyHistoryReplace(target);

  if (navigate) {
    navigate(target, { replace: true });
  }
};

/**
 * Launches the next game selected from "Recently Played".
 * Syncs the router URL in-place, then soft-reloads the game client iframe.
 */
export const launchNextGame = async (
  reload?: SoftReloadFn,
  navigate?: NavigateFunction,
): Promise<void> => {
  const { recentlyPlayedContext } = useStore.getState();
  const { nextGameUrl, nextGameUid } = recentlyPlayedContext;

  trackPlayNow(nextGameUrl);

  if (!nextGameUrl || !nextGameUid) {
    return;
  }

  syncNextGameRoute(nextGameUid, navigate);
  // Soft reload bumps gameResetRevision (and clears stale game state) so GLS
  // re-runs for the new route gameUid.
  reload?.({ preset: SOFT_RELOAD_PRESET.LAUNCH_NEW_GAME });
};
